import { toast } from "sonner";
import { useChatContext } from "stream-chat-react";
import { useAcceptChatChannelMutation } from "Api/Mutations/Chat/useAcceptChatChannelMutation";
import { useRejectChatChannelMutation } from "Api/Mutations/Chat/useRejectChatChannelMutation";
import { Resources, useResource } from "Translations/Resources";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import { useChatCategory } from "Components/Chat/ChatCategoryContext";
import type { ChatChannelCategory } from "Components/Chat/chatChannelCategories";

const RESOURCES = Resources.Chat.Requests;
const DEFAULT_CATEGORY: ChatChannelCategory = "All";

type Props = {
  channelId: string | undefined;
  shouldNavigateOnAccept?: boolean;
};

export const useChatRequestActions = ({
  channelId,
  shouldNavigateOnAccept,
}: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { client, setActiveChannel } = useChatContext();
  const { setSelectedCategory } = useChatCategory();

  const { mutate: acceptChannel, isPending: isAccepting } =
    useAcceptChatChannelMutation({
      onSuccess: () => {
        if (channelId && shouldNavigateOnAccept) {
          const channel = client.channel("messaging", channelId);
          setActiveChannel(channel);
          setSelectedCategory(DEFAULT_CATEGORY);
          navigate(`${appUrl("chat")}/${channelId}`, { replace: true });
        }
      },
      onError: () => toast.error(t(RESOURCES.AcceptError)),
    });

  const { mutate: rejectChannel, isPending: isRejecting } =
    useRejectChatChannelMutation({
      onSuccess: async () => {
        if (channelId) {
          try {
            await client.channel("messaging", channelId).mute();
          } catch {
            // Mute is best-effort — don't block the reject flow
          }
        }
        setActiveChannel(undefined);
        navigate(appUrl("chat"), { replace: true });
      },
      onError: () => toast.error(t(RESOURCES.RejectError)),
    });

  const { mutate: joinRejectedChannel, isPending: isJoining } =
    useAcceptChatChannelMutation({
      onSuccess: async () => {
        if (channelId) {
          try {
            await client.channel("messaging", channelId).unmute();
          } catch {
            // Unmute is best-effort
          }
          const channel = client.channel("messaging", channelId);
          setActiveChannel(channel);
          setSelectedCategory(DEFAULT_CATEGORY);
          navigate(`${appUrl("chat")}/${channelId}`, { replace: true });
        }
      },
      onError: () => toast.error(t(RESOURCES.JoinError)),
    });

  const isPending = isAccepting || isRejecting || isJoining;

  const handleAccept = () => {
    if (channelId) {
      acceptChannel(channelId);
    }
  };

  const handleReject = () => {
    if (channelId) {
      rejectChannel(channelId);
    }
  };

  const handleJoinRejected = () => {
    if (channelId) {
      joinRejectedChannel(channelId);
    }
  };

  return { handleAccept, handleReject, handleJoinRejected, isPending };
};
